import { poundsToPence } from '../invoicing/money.js';

export type DurationType = 'full_day' | 'half_day' | 'custom';

export type RateClient = {
  day_rate: number | string;
  half_day_rate: number | string;
  hourly_rate: number | string | null;
};

export type RateOverride = {
  day_rate: number | string;
  half_day_rate: number | string;
  hourly_rate: number | string | null;
};

export type RateJob = {
  duration_type: DurationType;
  custom_hours: number | string | null;
  custom_amount: number | string | null;
};

export class RateResolutionError extends Error {
  readonly code = 'rate_not_configured';

  constructor(message: string) {
    super(message);
    this.name = 'RateResolutionError';
  }
}

/**
 * Hours → integer millis of an hour (1.5h → 1500) from a decimal string/number
 * with at most 3 fractional digits. Avoids float money multiply.
 */
export function hoursToMillis(v: number | string): number | null {
  const raw = typeof v === 'number' ? String(v) : String(v).trim();
  if (!/^\d+(\.\d{1,3})?$/.test(raw)) return null;
  const [w, f = ''] = raw.split('.');
  const frac = (f + '000').slice(0, 3);
  return Number(w) * 1000 + Number(frac);
}

/**
 * Sole rate-resolution function. Returns integer pence.
 * Precedence: client_rate_overrides → client defaults → custom amount/hours.
 */
export function resolveRate(
  job: RateJob,
  client: RateClient,
  override: RateOverride | null,
): { ratePence: number; amountPence: number } {
  const source = override ?? client;

  if (job.duration_type === 'full_day') {
    const ratePence = poundsToPence(source.day_rate);
    if (ratePence === null) {
      throw new RateResolutionError('No day rate configured for this client');
    }
    return { ratePence, amountPence: ratePence };
  }

  if (job.duration_type === 'half_day') {
    const ratePence = poundsToPence(source.half_day_rate);
    if (ratePence === null) {
      throw new RateResolutionError(
        'No half-day rate configured for this client',
      );
    }
    return { ratePence, amountPence: ratePence };
  }

  const customAmount = poundsToPence(job.custom_amount);
  if (customAmount !== null) {
    return { ratePence: customAmount, amountPence: customAmount };
  }

  if (job.custom_hours === null || job.custom_hours === undefined) {
    throw new RateResolutionError(
      'Custom duration requires custom_hours or custom_amount',
    );
  }

  const hoursMillis = hoursToMillis(job.custom_hours);
  if (hoursMillis === null) {
    throw new RateResolutionError('Invalid custom_hours');
  }

  const hourly = poundsToPence(source.hourly_rate);
  if (hourly === null) {
    throw new RateResolutionError(
      'No hourly rate configured for custom-hours job',
    );
  }

  // amount = hourly_pence * hours, round half away: (hourly * millis + 500) / 1000
  const sign = hourly < 0 ? -1 : 1;
  const product = Math.abs(hourly) * hoursMillis;
  const amountPence = sign * Math.trunc((product + 500) / 1000);
  return { ratePence: hourly, amountPence };
}
